import { Box, Divider } from "@mui/material";
import React, { useEffect } from "react";
import type { EntityClickHandler } from "../../types/entityRegistry";
import { EntityDetailResponse, MainEntity } from "../../types/entityTypes";
import {
  EntityDetailComingSoon,
  EntityDetailStatus,
} from "./EntityDetailStatus";
import EntityContentHeader from "./EntityDetailOverlay/EntityContentHeader";
import FacilityDetail from "./EntityDetailOverlay/EntityDetails/FacilityDetail";
import OrganizationDetail from "./EntityDetailOverlay/EntityDetails/OrganizationDetail";
import ProjectDetail from "./EntityDetailOverlay/EntityDetails/ProjectDetail";
import SourceDetail from "./EntityDetailOverlay/EntityDetails/SourceDetail";
import EntityFunctionalHeader from "./EntityDetailOverlay/EntityFunctionalHeader";
import EntityRelationshipTabs from "./EntityDetailOverlay/EntityRelationshipTabs";

interface EntityDetailContentProps {
  entityDetailResponse: EntityDetailResponse | null;
  mainEntity: MainEntity | null;
  loading: boolean;
  error: string | null;
  onEntityClick?: EntityClickHandler;
  onClose?: () => void;
  mode?: "overlay" | "standalone";
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Presentation component for entity details
 * Renders entity header, details, and relationship tabs
 * Can be used in both overlay and standalone contexts
 */
const EntityDetailContent: React.FC<EntityDetailContentProps> = ({
  entityDetailResponse,
  mainEntity,
  loading,
  error,
  onEntityClick,
  onClose,
  mode = "overlay",
  scrollContainerRef,
}) => {
  // Reset scroll position to top when entity data changes
  // Use setTimeout to ensure DOM has updated (matches original overlay behavior)
  useEffect(() => {
    if (!loading && entityDetailResponse && scrollContainerRef?.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [entityDetailResponse, loading, scrollContainerRef]);

  // Loading state, then error state
  if (loading || error) {
    return <EntityDetailStatus loading={loading} error={error} mode={mode} />;
  }

  // No data state
  if (!entityDetailResponse || !mainEntity) {
    return null;
  }

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        flex: 1,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Functional Header - Always Full Width */}
      <EntityFunctionalHeader
        mainEntity={mainEntity}
        onClose={onClose || (() => {})}
        mode={mode}
      />

      {/* Content Header - Centered in standalone mode */}
      <Box
        sx={{
          display: "flex",
          justifyContent: mode === "standalone" ? "center" : "flex-start",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: mode === "standalone" ? "1200px" : "100%",
          }}
        >
          <EntityContentHeader mainEntity={mainEntity} />
        </Box>
      </Box>

      {/* Main Content - Centered in standalone mode */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: mode === "standalone" ? "center" : "stretch",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: mode === "standalone" ? "1200px" : "100%",
          }}
        >
          {/* Section 1: Entity Overview */}
          <Box sx={{ p: 2, flexShrink: 0 }}>
            {/* Static Attributes Grid */}
            {mainEntity &&
              entityDetailResponse &&
              (mainEntity.entityType === "facility" &&
              entityDetailResponse.f ? (
                <FacilityDetail
                  detailedFacility={entityDetailResponse as any}
                  onEntityClick={onEntityClick}
                />
              ) : mainEntity.entityType === "organization" &&
                entityDetailResponse.o ? (
                <OrganizationDetail
                  detailedOrganization={entityDetailResponse as any}
                  onEntityClick={onEntityClick}
                />
              ) : mainEntity.entityType === "project" &&
                entityDetailResponse.p ? (
                <ProjectDetail
                  detailedProject={entityDetailResponse as any}
                  onEntityClick={onEntityClick}
                />
              ) : mainEntity.entityType === "source" &&
                entityDetailResponse.s ? (
                <SourceDetail
                  detailedSource={entityDetailResponse as any}
                  onEntityClick={onEntityClick}
                />
              ) : (
                <EntityDetailComingSoon entityType={mainEntity.entityType} />
              ))}
          </Box>

          <Divider />
          {entityDetailResponse && (
            <EntityRelationshipTabs
              detailedEntity={entityDetailResponse as any}
              onEntityClick={onEntityClick}
              context={mainEntity?.entityType}
              mode={mode}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EntityDetailContent;
